import type { BotIntro, Bot, ChatClient, Message, MessagePart, Session, Source } from './types'
import { getApiClient } from './http'

type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

interface ApiEndpoint<Args extends unknown[] = []> {
  method: ApiMethod
  path: (...args: Args) => string
}

export const apiRoutes = {
  auth: {
    exchangeCasTicket: { method: 'POST', path: () => '/api/auth/cas/exchange' } satisfies ApiEndpoint,
    logout: { method: 'POST', path: () => '/api/auth/logout' } satisfies ApiEndpoint,
  },
  bots: {
    greeting: {
      method: 'GET',
      path: (botId: string) => `/api/bots/${botId}/greeting`,
    } satisfies ApiEndpoint<[string]>,
    sessions: {
      method: 'POST',
      path: (botId: string) => `/api/bots/${botId}/sessions`,
    } satisfies ApiEndpoint<[string]>,
    messages: {
      method: 'POST',
      path: (botId: string) => `/api/bots/${botId}/messages`,
    } satisfies ApiEndpoint<[string]>,
    sessionMessages: {
      method: 'GET',
      path: (botId: string, sessionId: string, limit = 200) =>
        `/api/bots/${botId}/sessions/${sessionId}/messages?limit=${limit}`,
    } satisfies ApiEndpoint<[string, string, number?]>,
    session: {
      method: 'PATCH',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}`,
    } satisfies ApiEndpoint<[string, string]>,
    deleteSession: {
      method: 'DELETE',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}`,
    } satisfies ApiEndpoint<[string, string]>,
  },
  sessions: {
    list: {
      method: 'GET',
      path: (limit = 50) => `/api/sessions?limit=${limit}`,
    } satisfies ApiEndpoint<[number?]>,
  },
  files: {
    prepareUpload: { method: 'POST', path: () => '/api/files/uploads' } satisfies ApiEndpoint,
    completeUpload: {
      method: 'POST',
      path: (fileId: string) => `/api/files/${fileId}/complete`,
    } satisfies ApiEndpoint<[string]>,
    delete: {
      method: 'DELETE',
      path: (fileId: string) => `/api/files/${fileId}`,
    } satisfies ApiEndpoint<[string]>,
  },
} as const

interface ApiSession {
  id: string
  title: string
  botId: string
  botName: string
  botType?: Bot['type']
  starred?: boolean
  createdAt: string
  updatedAt: string
}

interface ApiMessage {
  id: string
  sessionId: string
  role: 'user' | 'assistant'
  textContent: string
  parts?: MessagePart[]
  sources: Source[]
  createdAt: string
  feedbackRating?: FeedbackRating
}

interface ListSessionsResponse {
  sessions: ApiSession[]
  nextCursor?: string
}

export type FeedbackRating = 'good' | 'bad'

export interface BotMessageFeedbackPayload {
  botId: string
  sessionId: string
  messageId: string
  rating: FeedbackRating
  feedback?: string
  notifyMe?: boolean
  email?: string
}

interface ListMessagesResponse {
  messages: ApiMessage[]
  nextCursor?: string
}

function toSession(session: ApiSession): Session {
  return {
    id: session.id,
    title: session.title,
    botId: session.botId,
    botName: session.botName,
    botType: session.botType,
    starred: session.starred ?? false,
    createdAt: new Date(session.createdAt),
    updatedAt: new Date(session.updatedAt),
  }
}

function toMessage(message: ApiMessage): Message {
  const parts = message.parts?.length
    ? message.parts
    : [{
        type: 'text',
        content: message.textContent,
        format: message.role === 'assistant' ? 'markdown' : 'plain',
      } satisfies MessagePart]

  return {
    id: message.id,
    sessionId: message.sessionId,
    role: message.role,
    parts,
    sources: message.sources?.length ? message.sources : undefined,
    createdAt: new Date(message.createdAt),
    feedbackRating: message.feedbackRating,
  }
}

async function requestApi<T>(
  url: string,
  init?: { method?: ApiMethod; body?: unknown; signal?: AbortSignal },
): Promise<T> {
  const response = await getApiClient().request<T>({
    url,
    method: init?.method ?? 'GET',
    data: init?.body,
    signal: init?.signal,
  })
  return response.data
}

export async function fetchBotIntro(botId: string, _baseUrl?: string, signal?: AbortSignal): Promise<BotIntro> {
  const endpoint = apiRoutes.bots.greeting
  const response = await getApiClient().request<BotIntro>({
    url: endpoint.path(botId),
    method: endpoint.method,
    signal,
  })
  return response.data
}

export async function submitBotMessageFeedback(payload: BotMessageFeedbackPayload): Promise<void> {
  await requestApi<{ submitted: boolean }>(
    `/api/bots/${payload.botId}/sessions/${payload.sessionId}/messages/${payload.messageId}/feedback`,
    {
      method: 'POST',
      body: {
        rating: payload.rating,
        feedback: payload.feedback,
        notifyMe: payload.notifyMe ?? false,
        email: payload.email,
      },
    },
  )
}

export function createChatClient(): ChatClient {
  const sessionCache = new Map<string, ApiSession>()

  return {
    getBots(): Bot[] {
      const seen = new Set<string>()
      const bots: Bot[] = []

      for (const session of sessionCache.values()) {
        if (seen.has(session.botId)) continue
        seen.add(session.botId)
        bots.push({
          id: session.botId,
          name: session.botName,
          type: session.botType ?? 'departmental',
        })
      }

      return bots
    },

    getBot(botId: string): Bot | undefined {
      for (const session of sessionCache.values()) {
        if (session.botId === botId) {
          return {
            id: session.botId,
            name: session.botName,
            type: session.botType ?? 'departmental',
          }
        }
      }

      return undefined
    },

    async getSessions(signal?: AbortSignal): Promise<Session[]> {
      const endpoint = apiRoutes.sessions.list
      const data = await requestApi<ListSessionsResponse>(endpoint.path(50), {
        method: endpoint.method,
        signal,
      })
      const sessions = data.sessions ?? []

      for (const session of sessions) {
        sessionCache.set(session.id, session)
      }

      return sessions.map(toSession)
    },

    async getMessages(botId: string, sessionId: string, signal?: AbortSignal): Promise<Message[]> {
      const data = await requestApi<ListMessagesResponse>(
        apiRoutes.bots.sessionMessages.path(botId, sessionId, 200),
        {
          method: apiRoutes.bots.sessionMessages.method,
          signal,
        },
      )
      return (data.messages ?? []).map(toMessage)
    },

    async createSession(botId: string, title?: string): Promise<Session> {
      const session = await requestApi<ApiSession>(
        apiRoutes.bots.sessions.path(botId),
        {
          method: apiRoutes.bots.sessions.method,
          body: { title: title ?? 'New Chat' },
        },
      )

      sessionCache.set(session.id, session)
      return toSession(session)
    },

    async sendMessage(_sessionId: string, _parts: MessagePart[]): Promise<Message> {
      throw new Error('[chatClient] Use SseStreamingChatClient for sending messages')
    },

    async deleteSession(botId: string, sessionId: string): Promise<void> {
      await requestApi<{ deleted: boolean }>(
        apiRoutes.bots.deleteSession.path(botId, sessionId),
        {
          method: apiRoutes.bots.deleteSession.method,
        },
      )
      sessionCache.delete(sessionId)
    },

    async greet(botId: string): Promise<{ session: Session; message: Message }> {
      const session = await this.createSession(botId, 'New Chat')
      return {
        session,
        message: {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'assistant',
          parts: [],
          createdAt: new Date(),
        },
      }
    },

    async toggleStar(botId: string, sessionId: string): Promise<{ starred: boolean; limitReached?: boolean }> {
      const cachedSession = sessionCache.get(sessionId)
      const nextStarred = !(cachedSession?.starred ?? false)
      const updatedSession = await requestApi<ApiSession>(
        apiRoutes.bots.session.path(botId, sessionId),
        {
          method: apiRoutes.bots.session.method,
          body: { starred: nextStarred },
        },
      )

      sessionCache.set(sessionId, {
        ...(cachedSession ?? updatedSession),
        ...updatedSession,
        starred: updatedSession.starred ?? nextStarred,
      })
      return { starred: updatedSession.starred ?? nextStarred }
    },

    async updateSession(botId: string, sessionId: string, updates: { title?: string; starred?: boolean }): Promise<Session> {
      const updatedSession = await requestApi<ApiSession>(
        apiRoutes.bots.session.path(botId, sessionId),
        {
          method: apiRoutes.bots.session.method,
          body: updates,
        },
      )
      sessionCache.set(sessionId, updatedSession)
      return toSession(updatedSession)
    },
  }
}
